import { z } from "zod";
import {
    LayoutValues,
    layoutOwnValuesSchema,
} from "@common/schemas/domains/reportSettings/layout-setting.schema";
import {
    NumberFormatValues,
    numberFormatOwnValuesSchema,
} from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { reportSettingsOwnerSchema } from "@common/schemas/domains/reportSettings/report-settings-owner.schema";
import { ReportSettingsReportKey } from "@common/types/reports/report-settings-report-key";

export const REPORT_SETTING_KEYS = [
    "header",
    "title",
    "subtitle",
    "subtitle2",
    "footer",
    "memo",
    "watermarkText",
    "logoOption",
    "rowVisibility",
    "decimalPlaces",
    "negativeFormat",
    "isNegativeInRed",
    "hasThousandsSeparator",
    "zeroDisplay",
    "currencySymbol",
    "currencyPlacement",
    "percentDecimalPlaces",
    "amountScale",
] as const satisfies ReadonlyArray<keyof (LayoutValues & NumberFormatValues)>;

export type ReportSettingKey = (typeof REPORT_SETTING_KEYS)[number];

export type ReportSettingValues = LayoutValues & NumberFormatValues;

export type StoredReportSettingValues = {
    [K in ReportSettingKey]: ReportSettingValues[K] | null;
};

export type ReportSettingsStoredColumns = Omit<
    StoredReportSettingValues,
    "decimalPlaces"
> & { decimalPlaces: number | null };

export type ReportSettingsOwner = z.infer<typeof reportSettingsOwnerSchema>;

export type ReportSettingsStoredRow = ReportSettingsStoredColumns & {
    owner: ReportSettingsOwner;
    reportKey: ReportSettingsReportKey;
};

export const EMPTY_REPORT_SETTING_VALUES: { [K in ReportSettingKey]: null } = {
    header: null,
    title: null,
    subtitle: null,
    subtitle2: null,
    footer: null,
    memo: null,
    watermarkText: null,
    logoOption: null,
    rowVisibility: null,
    decimalPlaces: null,
    negativeFormat: null,
    isNegativeInRed: null,
    hasThousandsSeparator: null,
    zeroDisplay: null,
    currencySymbol: null,
    currencyPlacement: null,
    percentDecimalPlaces: null,
    amountScale: null,
};

const readOrNull = <T, V>(schema: z.ZodType<T>, value: V): T | null => {
    const result = schema.safeParse(value);
    return result.success ? result.data : null;
};

export const readReportSettingValues = (
    columns: ReportSettingsStoredColumns,
): StoredReportSettingValues => ({
    header: columns.header,
    title: columns.title,
    subtitle: columns.subtitle,
    subtitle2: columns.subtitle2,
    footer: columns.footer,
    memo: columns.memo,
    watermarkText: columns.watermarkText,
    logoOption: readOrNull(
        layoutOwnValuesSchema.shape.logoOption,
        columns.logoOption,
    ),
    rowVisibility: readOrNull(
        layoutOwnValuesSchema.shape.rowVisibility,
        columns.rowVisibility,
    ),
    decimalPlaces: readOrNull(
        numberFormatOwnValuesSchema.shape.decimalPlaces,
        columns.decimalPlaces,
    ),
    negativeFormat: readOrNull(
        numberFormatOwnValuesSchema.shape.negativeFormat,
        columns.negativeFormat,
    ),
    isNegativeInRed: columns.isNegativeInRed,
    hasThousandsSeparator: columns.hasThousandsSeparator,
    zeroDisplay: readOrNull(
        numberFormatOwnValuesSchema.shape.zeroDisplay,
        columns.zeroDisplay,
    ),
    currencySymbol: readOrNull(
        numberFormatOwnValuesSchema.shape.currencySymbol,
        columns.currencySymbol,
    ),
    currencyPlacement: readOrNull(
        numberFormatOwnValuesSchema.shape.currencyPlacement,
        columns.currencyPlacement,
    ),
    percentDecimalPlaces: readOrNull(
        numberFormatOwnValuesSchema.shape.percentDecimalPlaces,
        columns.percentDecimalPlaces,
    ),
    amountScale: readOrNull(
        numberFormatOwnValuesSchema.shape.amountScale,
        columns.amountScale,
    ),
});

export const pickReportSettingsColumns = (
    source: ReportSettingsStoredColumns,
): ReportSettingsStoredColumns => ({
    header: source.header,
    title: source.title,
    subtitle: source.subtitle,
    subtitle2: source.subtitle2,
    footer: source.footer,
    memo: source.memo,
    watermarkText: source.watermarkText,
    logoOption: source.logoOption,
    rowVisibility: source.rowVisibility,
    decimalPlaces: source.decimalPlaces,
    negativeFormat: source.negativeFormat,
    isNegativeInRed: source.isNegativeInRed,
    hasThousandsSeparator: source.hasThousandsSeparator,
    zeroDisplay: source.zeroDisplay,
    currencySymbol: source.currencySymbol,
    currencyPlacement: source.currencyPlacement,
    percentDecimalPlaces: source.percentDecimalPlaces,
    amountScale: source.amountScale,
});
